<template>
  <div class="page_content">
    <HcpInboxNavigation />
    <div>
      <div class="title_container">
        <h3
          ref="orderingExitTarget"
          data-test="classic-root-order-exit-target"
          tabindex="-1"
        >
          {{ $t("workspacesOverview.title") }}
        </h3>

        <div class="title_actions">
          <template v-if="workspaceOrder.editing.value">
            <span
              v-if="workspaceOrder.status.value !== 'idle'"
              data-test="workspace-order-status"
              class="ordering_status"
              :class="`is_${workspaceOrder.status.value}`"
              role="status"
            >
              {{ orderingStatusText }}
            </span>
            <button
              v-if="workspaceOrder.status.value === 'error'"
              data-test="workspace-order-retry"
              type="button"
              class="button ordering_retry"
              :disabled="workspaceOrder.saving.value"
              @click="retryOrdering"
            >
              {{ $t("workspace.ordering.retry") }}
            </button>
            <button
              type="button"
              class="button primary ordering_done"
              data-test="workspace-order-done"
              :disabled="
                workspaceOrder.saving.value ||
                workspaceOrder.gestureActive.value
              "
              @click="finishOrdering"
            >
              {{ $t("workspace.ordering.done") }}
            </button>
          </template>
          <template v-else>
            <button
              v-if="workspaceOrder.eligible.value"
              type="button"
              class="button secondary ordering_edit"
              data-test="workspace-order-button"
              @click="startOrdering"
            >
              {{ $t("workspace.ordering.edit") }}
            </button>
            <button
              v-if="canCreateWorkspace && workspaces.length > 0"
              class="button primary"
              @click="displayCreateWorkspaceModal = true"
            >
              <span class="material-symbols-outlined">add</span>
              {{ $t("workspacesOverview.createNew") }}
            </button>
          </template>
        </div>
      </div>

      <div v-if="renderedWorkspaces.length === 0" class="empty_pane">
        <div v-if="canCreateWorkspace">
          <span class="material-symbols-outlined"> post_add </span>

          <h2>{{ $t("workspacesOverview.emptyState.title") }}</h2>
          <p class="info">
            {{ $t("workspacesOverview.emptyState.info") }}
          </p>
          <button
            class="button primary"
            @click="displayCreateWorkspaceModal = true"
          >
            {{ $t("workspacesOverview.createNew") }}
          </button>
        </div>
        <div v-else>
          <h2>{{ $t("workspacesOverview.emptyState.title") }}</h2>
        </div>
      </div>

      <div v-else>
        <p v-if="workspaceOrder.editing.value" class="ordering_instructions">
          {{ $t("workspace.ordering.instructions") }}
        </p>
        <WorkspaceOrderList
          data-test="classic-root-order-list"
          :items="workspaceOrder.items.value"
          :editing="workspaceOrder.editing.value"
          :disabled="workspaceOrder.saving.value"
          :handle-label="$t('workspace.ordering.moveHandle')"
          :instructions="$t('workspace.ordering.instructions')"
          @move="workspaceOrder.move"
          @gesture="workspaceOrder.setGestureActive"
        >
          <template #item="{ item: workspace, canDrag, handleProps }">
            <div class="workspace_row">
              <button
                v-if="workspaceOrder.editing.value && canDrag"
                type="button"
                class="workspace_drag_handle"
                v-bind="handleProps"
              >
                <span class="material-symbols-outlined">drag_indicator</span>
              </button>
              <span
                v-else-if="workspaceOrder.editing.value"
                class="workspace_drag_placeholder"
                aria-hidden="true"
              />
              <div class="workspace_name">
                <router-link
                  :to="{
                    name: 'Workspace',
                    params: { workspaceId: workspace.id },
                  }"
                >
                  {{ workspace.name }}
                </router-link>
              </div>
            </div>
          </template>
        </WorkspaceOrderList>
      </div>
    </div>
  </div>

  <create-workspace-modal
    v-if="displayCreateWorkspaceModal"
    @close="displayCreateWorkspaceModal = false"
    @success="redirectToWorkspace"
  />
</template>

<script lang="ts" setup>
import { isOrdinaryWorkspace } from "@/hcp/inbox";
import HcpInboxNavigation from "@/hcp/HcpInboxNavigation.vue";
import { computed, nextTick, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import CreateWorkspaceModal from "@/components/CreateWorkspaceModal.vue";
import WorkspaceOrderList from "@/components/workspace-ordering/WorkspaceOrderList.vue";
import {
  RootWorkspaceOrderRefreshDocument,
  type RootWorkspaceOrderRefreshResult,
  useWorkspaceOrdering,
} from "@/composables/useWorkspaceOrdering";
import graphqlClient from "@/graphql/client";
import type { MyRootWorkspacesQueryVariables } from "@/graphql/operations";
import { useI18n } from "vue-i18n";

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const workspaces = computed(() => store.getters.rootWorkspaces.filter(isOrdinaryWorkspace));
const workspaceOrder = useWorkspaceOrdering(workspaces, {
  onCanonicalOrder: (items) => store.commit("setRootWorkspaces", items),
  refresh: async () => {
    const result = await graphqlClient
      .query<
        RootWorkspaceOrderRefreshResult,
        MyRootWorkspacesQueryVariables
      >(RootWorkspaceOrderRefreshDocument, {}, { requestPolicy: "network-only" })
      .toPromise();
    if (result.error) throw result.error;
    if (result.data?.workspaces === null) return [];
    return result.data?.workspaces?.nodes?.filter(
      (workspace): workspace is NonNullable<typeof workspace> =>
        isOrdinaryWorkspace(workspace),
    );
  },
});
const orderingExitTarget = ref<HTMLElement | null>(null);
const renderedWorkspaces = computed(() =>
  workspaceOrder.editing.value ? workspaceOrder.items.value : workspaces.value,
);
const orderingStatusText = computed(() => {
  switch (workspaceOrder.status.value) {
    case "saving":
      return t("workspace.ordering.saving");
    case "saved":
      return t("workspace.ordering.saved");
    case "error":
      return t("workspace.ordering.error");
    default:
      return "";
  }
});

async function startOrdering() {
  if (!workspaceOrder.start()) return;
  await focusOrderingHandle();
}

async function focusOrderingHandle(targetId?: string | null) {
  await nextTick();
  await nextTick();
  const scope = document.querySelector<HTMLElement>(
    '[data-test="classic-root-order-list"]',
  );
  const target = targetId
    ? Array.from(
        scope?.querySelectorAll<HTMLElement>("[data-workspace-order-id]") ?? [],
      ).find((item) => item.dataset.workspaceOrderId === targetId)
    : null;
  (target ?? scope)
    ?.querySelector<HTMLButtonElement>(
      '[data-workspace-order-handle][aria-disabled="false"]',
    )
    ?.focus();
}

async function focusOrderingExitTarget() {
  await nextTick();
  await nextTick();
  const button = document.querySelector<HTMLElement>(
    '[data-test="workspace-order-button"]',
  );
  (button ?? orderingExitTarget.value)?.focus();
}

async function focusOrderingExitTargetIfLost() {
  await nextTick();
  await nextTick();
  if (document.activeElement === document.body) {
    await focusOrderingExitTarget();
  }
}

watch(workspaceOrder.editing, (editing, wasEditing) => {
  if (wasEditing && !editing) {
    void focusOrderingExitTargetIfLost();
  }
});

watch(workspaceOrder.eligible, (eligible, wasEligible) => {
  const button = document.querySelector<HTMLElement>(
    '[data-test="workspace-order-button"]',
  );
  const invalidatedFocusedButton =
    wasEligible && !eligible && document.activeElement === button;
  if (invalidatedFocusedButton) void focusOrderingExitTarget();
});

async function retryOrdering() {
  const targetId = workspaceOrder.failedTargetId.value;
  await focusOrderingHandle(targetId);
  await workspaceOrder.retry();
  await focusOrderingHandle(targetId);
}

async function finishOrdering() {
  if (!workspaceOrder.done()) return;
  await focusOrderingExitTarget();
}

const canCreateWorkspace = computed(() =>
  store.getters.user.canCreateWorkspace(),
);

const displayCreateWorkspaceModal = ref<boolean>(false);
const redirectToWorkspace = (id: number) => {
  router.push({
    name: "Workspace",
    params: { workspaceId: id },
  });
};
</script>

<style lang="scss" scoped>
.page_content {
  height: 100%;
}

.title_container {
  display: flex;
  align-items: center;

  h3 {
    flex: 1;
    margin-top: 0;
    font-size: 1.1rem;
    font-weight: 500;
    line-height: 38px;
  }
}

.title_actions {
  align-items: center;
  display: flex;
  gap: 0.55rem;
}

.button.ordering_edit:focus-visible {
  outline: 2px solid var(--classic-focus, var(--classic-accent, #34b5ba));
  outline-offset: 2px;
}

.ordering_status {
  color: var(--classic-text-secondary, #666);
  font-size: 0.82rem;
}

.ordering_status.is_saved {
  color: var(--classic-success, #27844a);
}

.ordering_status.is_error {
  color: var(--classic-danger, #cf453e);
}

.ordering_retry {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}

.ordering_instructions {
  color: var(--classic-text-secondary, #666);
  font-size: 0.82rem;
  margin: 0 0 0.55rem;
}

.workspace_row {
  align-items: center;
  background: var(--classic-surface, #fff);
  border-bottom: 1px solid var(--classic-border, #dbdbdb);
  display: flex;
  min-height: 44px;
  padding: 0 0.65rem;
}

.workspace_row:hover {
  background: var(--classic-surface-hover, #f7f7f7);
}

.workspace_drag_handle,
.workspace_drag_placeholder {
  flex: 0 0 34px;
  width: 34px;
}

.workspace_drag_handle {
  align-items: center;
  background: transparent;
  border: 0;
  color: var(--classic-text-secondary, #777);
  cursor: grab;
  display: inline-flex;
  justify-content: center;
  padding: 0.35rem;
}

.workspace_drag_handle:active {
  cursor: grabbing;
}

@media (max-width: 767px), (any-pointer: coarse) {
  .workspace_drag_handle,
  .workspace_drag_placeholder {
    flex-basis: 44px;
    min-height: 44px;
    min-width: 44px;
  }

  .workspace_drag_handle,
  .workspace_drag_placeholder {
    width: 44px;
  }

  .ordering_retry,
  .ordering_done,
  .ordering_edit {
    flex: 0 0 auto;
    min-height: 44px;
    min-width: 44px;
  }
}

.workspace_name {
  flex: 1;
  min-width: 0;
}

.workspace_name a {
  color: var(--classic-text, #444);
  display: block;
  font-weight: 500;
  overflow: hidden;
  padding: 0.75rem 0;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.workspace_name a:hover {
  text-decoration: underline;
}

.empty_pane {
  height: calc(100% - 75px);
  display: flex;
  justify-content: center;

  > div {
    flex: none;
    text-align: center;
    margin-top: 5em;
  }

  .material-symbols-outlined {
    font-size: 6em;
    opacity: 0.4;
  }

  h2 {
    margin-bottom: 0;
  }

  p {
    margin: 0.6em 0 1.5em 0;
  }
}

@media (max-width: 767px) {
  .title_container {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .title_container h3,
  .title_actions {
    width: 100%;
  }

  .title_actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .ordering_status {
    white-space: normal;
  }
}
</style>
